import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { List as IList, Map as IMap } from 'immutable';
import { Grid, Typography, Box } from '@mui/material';
import { injectIntl, intlShape } from 'react-intl';
import Markdown from 'markdown-to-jsx';
import DOMPurify from 'dompurify';

import CarouselContainer from './Carousel/CarouselContainer';
import { useFlow } from '../routing/FlowProvider';
import {
    getAllCategories,
    getDigitalCategories,
    getPhysicalCategories,
    hasDigitalCategories,
    hasPhysicalCategories,
    getDigitalPrimaryFaceplate,
    getPhysicalPrimaryFaceplate
} from '../landingPage/landingPageSelectors';
import {
    getBrandName,
    getBannerData,
    getBrandElectronicCurrencyList,
    getBrandPlasticCurrencyList,
    getEgcRangedCatalog,
    getPlasticRangedCatalog,
    getBrandSupportedCardTypes
} from '../brand/brandSelectors';
import { getBannerDisplayStatus, getPromoBanners } from '../promo/promoSelectors';
import PromoBanner from '../promo/PromoBanner';
import homePageV2Messages from './assets/messages/homePageMessages';
import { E_CARD_TYPE, PHYSICAL_CARD_TYPE } from '../productCartHandler/productCartModule';
import { getLoaderStatus } from '../loader/loaderSelector';
import SkeletonLoader from '../loader/SkeletonLoader';
import { getCheckOutSubHeaderTextDigital } from '../intl/intlSelectors';
import { getPreferredCardType, getIsSmartFlow } from '../routing/flowSelectors';
import { CARD_TYPES } from '../constants/constants';
import {
    selectedCardType
} from '../landingPage/landingPageModule';
import { getEnv } from '../app/bootstrap';
import Loader from '../loader/loader';
import useLandingPageLoader from './useLandingPageLoader';

const LandingPage = (props) => {
    const {
        intl, stepName, productDetails, brandName, digitalCategories, physicalCategories,
        supportedElectronicCurrencyList, supportedPlasticCurrencyList, isDigitalCategories,
        isPhysicalCategories, egcCatalogRange, plasticCatalogRange, digitalPrimaryFaceplate,
        isLoading, physicalPrimaryFacePlate, supportedCardTypes, checkoutSubHeaderText, banners,
        isPreferredCardTypePlastic, isSmartFlow, saveSelectedCardType
    } = props;
    const { goToNextStep } = useFlow();
    const handleSubmit = isPhysicalCardType => goToNextStep(stepName, {
        overwritingHistoryParams: {
            state: { isPhysicalCardType }
        }
    });

    
    
    
    const hasPromoBanner = banners !== null;
    const supportsDigital = supportedCardTypes.get('supportsDigitalCards');
    const supportsPhysical = supportedCardTypes.get('supportsPlasticCards');

    const digitalCarouselContainerProps = {
        ...props,
        handleSubmit,
        productDetails,
        categories: digitalCategories,
        giftCardMainTitle: intl.formatMessage(homePageV2Messages.digitalGiftCardTitle),
        submitButtonName: intl.formatMessage(homePageV2Messages.digitalGiftCardCTAButtonText),
        supportedCurrencyList: supportedElectronicCurrencyList,
        catalogRange: egcCatalogRange,
        giftCardDeliveryType: E_CARD_TYPE,
        digitalPrimaryFaceplate,
        isLoading
    };

    const physicalCarouselContainerProps = {
        ...props,
        handleSubmit,
        productDetails,
        categories: physicalCategories,
        giftCardMainTitle: intl.formatMessage(homePageV2Messages.physicalGiftCardTitle),
        submitButtonName: intl.formatMessage(homePageV2Messages.physicalGiftCardCTAButtonText),
        supportedCurrencyList: supportedPlasticCurrencyList,
        catalogRange: plasticCatalogRange,
        giftCardDeliveryType: PHYSICAL_CARD_TYPE,
        physicalPrimaryFacePlate,
        isLoading
    };

    
    
    
    
    
    
    

    useEffect(() => {
        if (isPreferredCardTypePlastic === null) return;

        if (isPreferredCardTypePlastic === false) {
            saveSelectedCardType(CARD_TYPES.Digital);
            handleSubmit(false);
        }
        if (isPreferredCardTypePlastic === true) {
            saveSelectedCardType(CARD_TYPES.Physical);
            handleSubmit(true);
        }
    }, [isPreferredCardTypePlastic]);

    const { showLoader } = useLandingPageLoader({
        isSmartFlow,
        isLoading,
        digitalCategories,
        physicalCategories,
        supportsDigital,
        supportsPhysical
    });

    if (showLoader) {
        return <Loader showTestEnv={getEnv() !== 'PROD'} />;
    }

    return (
        <Box className='landing-page-wrapper'>
            <Grid container spacing={2}>
                <div className='landing-page-header-banner'>
                    <Grid className='landing-page-header'>
                        <Grid item>
                            {}
                            <SkeletonLoader
                                isLoading={isLoading}
                                skeletonProps={{
                                    animation: 'wave',
                                    variant: 'rectangular',
                                    className: 'skeletonclass',
                                    height: 36
                                }}
                            >
                                <Typography variant="h1" className='landing-page-header-text'>
                                    <Markdown>
                                        {DOMPurify.sanitize(
                                            intl.formatMessage(
                                                homePageV2Messages.homePageV2HeaderTitle,
                                                { brandName }
                                            )
                                        )}
                                    </Markdown>
                                </Typography>
                            </SkeletonLoader>
                        </Grid>
                        {checkoutSubHeaderText && (
                            <Grid item>
                                {}
                                <SkeletonLoader
                                    isLoading={isLoading}
                                    skeletonProps={{
                                        animation: 'wave',
                                        variant: 'rectangular',
                                        className: 'skeletonclass',
                                        height: 36
                                    }}
                                >
                                    <Typography variant="h3" className='landing-page-header-subtext'>

                                        <Markdown>
                                            {DOMPurify.sanitize(checkoutSubHeaderText)}
                                        </Markdown>
                                    </Typography>
                                </SkeletonLoader>
                            </Grid>
                        )}
                    </Grid>
                    {hasPromoBanner && (
                        <Grid item className='promo-banner-container'>
                            {}
                            <SkeletonLoader
                                isLoading={isLoading}
                                skeletonProps={{
                                    animation: 'wave',
                                    variant: 'rectangular',
                                    className: 'skeletonclass',
                                    height: 85
                                }}
                            >
                                <PromoBanner />
                            </SkeletonLoader>
                        </Grid>
                    )}
                </div>
                <Grid item className='carousel' xs={12}>
                    {}
                    {isDigitalCategories && supportsDigital && (
                        <CarouselContainer {...digitalCarouselContainerProps} />
                    )}

                    {}
                    {isPhysicalCategories && supportsPhysical && (
                        <CarouselContainer {...physicalCarouselContainerProps} />
                    )}

                    {!supportsDigital && !supportsPhysical && (
                        <Box
                            className='error-container no-inventory-error-container'
                        >
                            <Typography variant="h5" className='no-inventory-message'>
                                {intl.formatMessage(homePageV2Messages.noInventoryMessage)}
                            </Typography>
                        </Box>
                    )}

                    {}
                    {}
                    {}

                </Grid>
            </Grid>
        </Box>
    );
};

LandingPage.defaultProps = {
    brandName: '',
    productDetails: null,
    supportedElectronicCurrencyList: IList(),
    supportedPlasticCurrencyList: IList(),
    digitalPrimaryFaceplate: IMap(),
    physicalPrimaryFacePlate: IMap(),
    supportedCardTypes: IMap(),
    checkoutSubHeaderText: '',
    banners: null
};

LandingPage.propTypes = {
    intl: intlShape.isRequired,
    stepName: PropTypes.string.isRequired,
    categories: PropTypes.instanceOf(IList).isRequired,
    bannerData: PropTypes.instanceOf(IList).isRequired,
    displayBanners: PropTypes.bool.isRequired,
    productDetails: PropTypes.object,
    brandName: PropTypes.string,
    digitalCategories: PropTypes.instanceOf(IList).isRequired,
    physicalCategories: PropTypes.instanceOf(IList).isRequired,
    supportedElectronicCurrencyList: PropTypes.instanceOf(IList),
    supportedPlasticCurrencyList: PropTypes.instanceOf(IList),
    isDigitalCategories: PropTypes.bool.isRequired,
    isPhysicalCategories: PropTypes.bool.isRequired,
    egcCatalogRange: PropTypes.object.isRequired,
    plasticCatalogRange: PropTypes.object.isRequired,
    digitalPrimaryFaceplate: PropTypes.instanceOf(IMap),
    isLoading: PropTypes.bool.isRequired,
    physicalPrimaryFacePlate: PropTypes.instanceOf(IMap),
    supportedCardTypes: PropTypes.instanceOf(IMap),
    checkoutSubHeaderText: PropTypes.string,
    banners: PropTypes.object,
    isPreferredCardTypePlastic: PropTypes.bool.isRequired,
    isSmartFlow: PropTypes.bool.isRequired,
    saveSelectedCardType: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    categories: getAllCategories(state),
    digitalCategories: getDigitalCategories(state),
    physicalCategories: getPhysicalCategories(state),
    bannerData: getBannerData(state),
    displayBanners: getBannerDisplayStatus(state),
    brandName: getBrandName(state),
    supportedElectronicCurrencyList: getBrandElectronicCurrencyList(state),
    supportedPlasticCurrencyList: getBrandPlasticCurrencyList(state),
    isDigitalCategories: hasDigitalCategories(state),
    isPhysicalCategories: hasPhysicalCategories(state),
    egcCatalogRange: getEgcRangedCatalog(state),
    plasticCatalogRange: getPlasticRangedCatalog(state),
    digitalPrimaryFaceplate: getDigitalPrimaryFaceplate(state),
    isLoading: getLoaderStatus(state),
    physicalPrimaryFacePlate: getPhysicalPrimaryFaceplate(state),
    supportedCardTypes: getBrandSupportedCardTypes(state),
    checkoutSubHeaderText: getCheckOutSubHeaderTextDigital(state),
    banners: getPromoBanners(state),
    isPreferredCardTypePlastic: getPreferredCardType(state),
    isSmartFlow: getIsSmartFlow(state)
});

const mapDispatchToProps = {
    saveSelectedCardType: selectedCardType
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(LandingPage));
